import { type ReactNode } from "react";

import { Link } from "react-router";

import {
  CircleAlertIcon,
  CircleCheckIcon,
  ExternalLinkIcon,
} from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { asIcon, Icon, IconData } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import type { SideNavComponentCollapsibleProps } from "./SideNavComponent";

export type SideNavLinkProps = {
  to?: string;
  external?: boolean;
  showExternalIcon?: boolean;
  onClick?: () => void;
  content: ReactNode;
  tooltip?: ReactNode;
  iconLeft?: IconData | ReactNode;
  iconLeftAlt?: ReactNode;
  iconRight?: IconData | ReactNode;
  active?: boolean;
  compact?: boolean | "very";
  inset?: boolean;
  secondary?: boolean;
  oneLine?: boolean;
  style?: React.CSSProperties;
};

// Shared geometry for expanded and collapsed links, so icons don't move when
// toggling: padding + icon slot + padding gives a square collapsed link.
const LINK_PADDING = 10;
const ICON_SLOT_SIZE = 20;
export const COLLAPSED_LINK_SIZE = LINK_PADDING * 2 + ICON_SLOT_SIZE;

const StyledSideNavLink = styled.div`
  .icons-left:not(:empty) {
    margin-right: 10px;
  }

  &.is-collapsed {
    margin: 0 !important;

    .icons-left {
      margin-right: 0px;
    }

    .sidebar-nav-link {
      width: ${COLLAPSED_LINK_SIZE}px;

      > div.content {
        display: none;
      }
    }
  }

  .sidebar-nav-link {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;

    > div.content {
      display: flex;
      flex: 1;
    }

    > div.content.is-one-line {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > .icons-right,
    .icons-left {
      display: inline-flex;
      align-items: center;
      flex: 0;
      min-height: ${ICON_SLOT_SIZE}px;
    }

    .icons-left:not(:empty) {
      min-width: ${ICON_SLOT_SIZE}px;
      justify-content: center;
    }
    text-transform: capitalize;
    justify-content: space-between;
    padding: ${LINK_PADDING}px;
    line-height: ${ICON_SLOT_SIZE}px;
    box-sizing: border-box;
    border-radius: 5px;

    &.is-inset {
      width: calc(100% - 30px);
      margin-left: 30px;
    }

    &:not(.is-inset) {
      width: 100%;
      margin-left: auto;
    }

    &.is-compact-very {
      font-size: 13.5px;
    }

    &:not(.is-compact-very) {
      font-size: 15px;
    }

    &.is-active {
      background-color: #d2dde5;
    }

    &:not(.is-active) {
      background-color: none;
    }

    &:hover {
      background-color: #eef1f2;
    }

    &.is-active:hover {
      background-color: #d2dde5;
    }

    .icon-alt {
      margin: 0 15px 0 2.5px;
    }

    > * {
      display: block;
    }

    > * {
      color: black !important;
      font-weight: normal;
    }

    &.is-secondary > * {
      color: gray !important;
    }

    &.is-active > * {
      font-weight: bold;
    }
  }
`;

const SideNavLinkContainerComponent = ({
  children,
  external,
  to,
  onClick,
}: {
  children: ReactNode;
  external?: boolean;
  to?: string;
  onClick?: () => void;
}) => {
  if (to) {
    if (external) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return (
      <Link to={to} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleClickForKeyboard(onClick)}
    >
      {children}
    </div>
  );
};

const SideNavLink = ({
  to,
  content,
  tooltip,
  iconLeft,
  iconLeftAlt,
  iconRight,
  active,
  onClick,
  compact,
  inset,
  external,
  showExternalIcon,
  isCollapsed,
  secondary,
  oneLine,
  style,
}: SideNavLinkProps & Partial<SideNavComponentCollapsibleProps>) => {
  const theme = useTheme();
  return (
    <Popup
      disabled={!theme.sizes.isComputer || (!tooltip && !isCollapsed)}
      position="right center"
      hoverable={!!tooltip}
      on={["hover", "focus"]}
      trigger={
        <StyledSideNavLink
          className={cx(
            theme.sizes.isComputer && isCollapsed && "is-collapsed",
          )}
        >
          <SideNavLinkContainerComponent
            external={external}
            to={to}
            onClick={onClick}
          >
            <div
              className={cx(
                "sidebar-nav-link",
                active && "is-active",
                compact === "very" && "is-compact-very",
                inset && "is-inset",
                secondary && "is-secondary",
              )}
              style={style}
            >
              <div className="icons-left">
                {iconLeftAlt ? (
                  <span className="icon-alt">{iconLeftAlt}</span>
                ) : (
                  asIcon(iconLeft)
                )}
              </div>
              <div className={cx("content", oneLine && "is-one-line")}>
                {!(theme.sizes.isComputer && isCollapsed) ? content : null}
              </div>
              {!(theme.sizes.isComputer && isCollapsed) ? (
                <div className="icons-right">
                  {external || showExternalIcon ? (
                    <Icon
                      icon={ExternalLinkIcon}
                      size="small"
                      style={{ marginLeft: 5, height: 17 }}
                    />
                  ) : null}{" "}
                  {asIcon(iconRight)}
                </div>
              ) : null}
            </div>
          </SideNavLinkContainerComponent>
        </StyledSideNavLink>
      }
    >
      {tooltip || content}
    </Popup>
  );
};

export default SideNavLink;
